import {
    ForwardedRef,
    forwardRef,
    memo,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";

import Hls, { HlsConfig } from "hls.js";

import { DateTime } from "luxon";

import { useAppSelector } from "app/hooks";

import {
    TransformWrapper,
    TransformComponent,
    ReactZoomPanPinchContentRef,
} from "react-zoom-pan-pinch";
import { selectInWindowMode } from "features/core/store/selectors";
import { MsalInstanceService } from "features/auth/MsalInstanceService";
import { selectStreamingApiScopes } from "features/config/store/selectors";
import { useTtlCounter } from "hooks/useTtlCounter";
import { PlayerRef } from "./types/PlayerRef";
import { Property } from "csstype";
import { PlayMode } from "./types/PlayMode";
import { VideoWithOverlay } from "./VideoWithOverlay";
import { HlsStats } from "./types/HlsStats";
import { StreamPlayerModeType } from "types/StreamPlayerModeType";

const hlsBaseConfig: Partial<HlsConfig> = {
    debug: false,
    enableWorker: true,
    lowLatencyMode: true,
};

const hlsLivestreamConfig: Partial<HlsConfig> = {
    liveSyncDuration: 1,
    liveMaxLatencyDuration: 2,
    liveDurationInfinity: true,
    maxLiveSyncPlaybackRate: 2,
    backBufferLength: 0,
};

const hlsPlaybackConfig: Partial<HlsConfig> = {
    maxBufferLength: 999,
    manifestLoadingTimeOut: 10 * 60 * 1000,
};

const hlsDiagnosticsConfig: Partial<HlsConfig> = {
    ...hlsLivestreamConfig,
    lowLatencyMode: false,
};

const reInitHlsIntervalInMilliseconds = 100;
const timeOutInitializationInMs = 1000 * 60 * 2; // 2 minutes

export interface HlsPlayerProps {
    url?: string;
    playMode: PlayMode;
    playSpeed: number;
    enableAudio: boolean;
    audioEnabledByDefault: boolean;
    onLoadedData?: () => void;
    onCanPlayThrough?: () => void;
    onWaiting?: () => void;
    onClickVideo?: () => void;
    videoMouseOverCursor?: Property.Cursor;
    onStreamQualityErrorCountChange: (count: number) => void;
    onStreamErrorOccured?: (error: string) => void;
    onAttachingFaultOccured?: () => void;
    playerMode: StreamPlayerModeType;
    onStatsChanged?: (stats: HlsStats) => void;
    diagnosticCorrelationId?: string;
    extraRequestHeaders?: Map<string, string>;
}

export const HlsPlayer = memo(
    forwardRef<PlayerRef, HlsPlayerProps>(
        (props: HlsPlayerProps, ref: ForwardedRef<PlayerRef>) => {
            const hlsInstance = useRef<Hls>(undefined);
            const videoElementRef = useRef<HTMLVideoElement>(null);
            const [hasHlsSupport] = useState(Hls.isSupported());
            const [isMuted, setIsMuted] = useState(false);

            // Items added can stay for 10 seconds
            const { addToCounter, count } = useTtlCounter(10);

            const streamingApiScopes = useAppSelector(selectStreamingApiScopes);
            const retryCountRef = useRef(0);
            const startDateTimeLoading = useRef<DateTime>(DateTime.now());

            const wasPlayingBeforeHiddenVisibility = useRef(false);

            const hlsConfig = useMemo(() => {
                const config = {
                    ...hlsBaseConfig,
                    ...(props.playerMode === StreamPlayerModeType.LiveStream &&
                        hlsLivestreamConfig),
                    ...(props.playerMode === StreamPlayerModeType.Playback &&
                        hlsPlaybackConfig),
                    ...(props.playerMode === StreamPlayerModeType.Diagnostic &&
                        hlsDiagnosticsConfig),
                    xhrSetup: async (xhr: XMLHttpRequest) => {
                        const accessToken =
                            await MsalInstanceService.getInstance().acquireTokenAsync(
                                streamingApiScopes,
                            );

                        xhr.setRequestHeader(
                            "Authorization",
                            `Bearer ${accessToken}`,
                        );

                        if (props.extraRequestHeaders) {
                            props.extraRequestHeaders.forEach((value, key) => {
                                xhr.setRequestHeader(key, value);
                            });
                        }

                        if (props.diagnosticCorrelationId) {
                            xhr.setRequestHeader(
                                "diagnostic-correlation-id",
                                `${props.diagnosticCorrelationId}`,
                            );
                        }
                    },
                } as Partial<HlsConfig>;

                return config;
            }, [
                props.playerMode,
                props.diagnosticCorrelationId,
                props.extraRequestHeaders,
            ]);

            useEffect(() => {
                const handleVisibilityChange = () => {
                    if (document.visibilityState === "hidden") {
                        // Pause the video and stop loading when the tab is inactive
                        if (videoElementRef.current) {
                            wasPlayingBeforeHiddenVisibility.current =
                                !videoElementRef.current.paused &&
                                !videoElementRef.current.ended;
                            videoElementRef.current.pause();
                        }
                        hlsInstance.current?.stopLoad();
                    } else if (document.visibilityState === "visible") {
                        // Resume loading and playback when the tab becomes active
                        if (
                            videoElementRef.current &&
                            wasPlayingBeforeHiddenVisibility.current
                        ) {
                            hlsInstance.current?.startLoad();
                            videoElementRef.current.play();
                        }
                    }
                };

                document.addEventListener(
                    "visibilitychange",
                    handleVisibilityChange,
                );

                return () => {
                    document.removeEventListener(
                        "visibilitychange",
                        handleVisibilityChange,
                    );
                };
            }, [props.playMode]);

            useEffect(() => {
                props.onStreamQualityErrorCountChange(count);
            }, [count]);

            useImperativeHandle(ref, () => {
                const url = props.url ?? "";
                return {
                    reloadStream: () => {
                        if (hlsInstance.current) {
                            console.log(`Reloading stream: ${url}`);
                            hlsInstance.current.loadSource(url);
                        }
                    },
                    videoSetCurrentTime(currentTime: number) {
                        if (videoElementRef.current) {
                            videoElementRef.current.currentTime = currentTime;
                        }
                    },
                    reinitializePlayer: () => {
                        if (hlsInstance.current && videoElementRef.current) {
                            retryCountRef.current = 0;
                            startDateTimeLoading.current = DateTime.now();

                            hlsInstance.current.stopLoad();
                            hlsInstance.current.detachMedia();

                            hlsInstance.current.attachMedia(
                                videoElementRef.current,
                            );
                        }
                    },
                    isReadyToPlay: () => {
                        return (
                            (videoElementRef.current &&
                                videoElementRef.current.readyState > 1) ??
                            false
                        );
                    },
                };
            }, [props.url]);

            useEffect(() => {
                const initHls = () => {
                    if (
                        hasHlsSupport &&
                        videoElementRef.current &&
                        streamingApiScopes
                    ) {
                        const hls = new Hls(hlsConfig);
                        hlsInstance.current = hls;

                        hls.on(Hls.Events.ERROR, (event, data) => {
                            if (props.onStreamErrorOccured) {
                                props.onStreamErrorOccured(data.details);
                            }

                            if (
                                data.details ===
                                Hls.ErrorDetails.BUFFER_STALLED_ERROR
                            ) {
                                addToCounter();
                            }

                            const startDateTimeLoadingDiff =
                                startDateTimeLoading.current.diffNow()
                                    .milliseconds * -1;
                            if (
                                startDateTimeLoadingDiff <
                                timeOutInitializationInMs
                            ) {
                                if (data.fatal) {
                                    if (retryCountRef.current % 10 === 0) {
                                        // log once in every 10 occurrences
                                        console.error(
                                            `Fatal network error encountered, trying to recover, attempt ${retryCountRef.current + 1} (max allowed time in ms ${timeOutInitializationInMs}, currently trying for ${startDateTimeLoadingDiff}ms), for ${props.url}`,
                                        );
                                    }

                                    retryCountRef.current++;
                                    setTimeout(() => {
                                        hls.recoverMediaError();
                                    }, reInitHlsIntervalInMilliseconds);
                                }
                            } else {
                                console.error(
                                    `Failed to recover in time (${timeOutInitializationInMs}ms) from fatal error, for ${props.url}`,
                                );
                                if (props.onAttachingFaultOccured) {
                                    props.onAttachingFaultOccured();
                                }
                            }
                        });

                        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                            if (props.url) {
                                hls.loadSource(props.url);
                            }
                        });

                        hls.on(Hls.Events.MANIFEST_PARSED, () => {
                            if (
                                videoElementRef.current &&
                                props.playMode === "autoPlay"
                            ) {
                                const videoElement =
                                    videoElementRef.current as HTMLVideoElement;
                                videoElement.play();
                            }
                        });

                        hls.attachMedia(videoElementRef.current);
                    }
                };

                initHls();

                // When subscribed, extract stats periodically and send stats to callback
                const extractHlsStats = () => {
                    if (props.onStatsChanged) {
                        const video = videoElementRef.current;
                        if (video && hlsInstance.current) {
                            const bufferLength =
                                video.buffered.length > 0
                                    ? video.buffered.end(0) -
                                      video.buffered.start(0)
                                    : 0;
                            const quality = video.getVideoPlaybackQuality();

                            props.onStatsChanged({
                                bandwidthEstimate:
                                    hlsInstance.current?.bandwidthEstimate,
                                bufferLength: bufferLength,
                                latency: hlsInstance.current?.latency,
                                droppedFrames: quality.droppedVideoFrames,
                                width: video.videoWidth,
                                height: video.videoHeight,
                            });
                        }
                    }
                };

                // refresh hls stats periodically
                const interval = setInterval(extractHlsStats, 500);

                return function cleanup() {
                    if (hlsInstance.current) {
                        hlsInstance.current.destroy();
                    }

                    clearInterval(interval);
                };
            }, [
                videoElementRef,
                streamingApiScopes,
                hasHlsSupport,
                hlsConfig,
                props.url,
            ]);

            // Used to control video for playback
            useEffect(() => {
                if (videoElementRef.current && props.playMode !== "autoPlay") {
                    const videoElement =
                        videoElementRef.current as HTMLVideoElement;

                    switch (props.playMode) {
                        case "play":
                            hlsInstance.current?.startLoad();
                            videoElement.play();
                            break;

                        case "pause":
                            videoElement.pause();
                            break;

                        case "stop":
                            videoElement.pause();
                            hlsInstance.current?.stopLoad();
                            break;
                    }
                }
            }, [videoElementRef, props.playMode, hlsConfig]);

            // On windowmode we must reset the zoom level
            const inWindowMode = useAppSelector(selectInWindowMode);
            const transformWrapperRef =
                useRef<ReactZoomPanPinchContentRef>(null);
            useEffect(() => {
                if (inWindowMode && transformWrapperRef.current) {
                    transformWrapperRef.current.resetTransform();
                }
            }, [inWindowMode]);

            return (
                <>
                    <TransformWrapper
                        ref={transformWrapperRef}
                        disabled={inWindowMode}
                        key={props.url}
                    >
                        {({ resetTransform }) => (
                            <TransformComponent
                                wrapperStyle={{
                                    width: "100%",
                                    height: "100%",
                                }}
                                contentStyle={{
                                    width: "100%",
                                    height: "100%",
                                }}
                            >
                                <VideoWithOverlay
                                    showOverlay={!inWindowMode}
                                    audioEnabledByDefault={
                                        props.audioEnabledByDefault
                                    }
                                    enableAudio={props.enableAudio}
                                    isMuted={isMuted}
                                    onSetIsMuted={(x) => setIsMuted(x)}
                                >
                                    <video
                                        ref={videoElementRef}
                                        playsInline
                                        autoPlay={props.playMode === "autoPlay"}
                                        muted={isMuted || !props.enableAudio}
                                        style={{
                                            backgroundColor: "#000",
                                            width: "100%",
                                            height: "100%",
                                            cursor:
                                                props.videoMouseOverCursor ||
                                                "initial",
                                        }}
                                        onCanPlayThrough={
                                            props.onCanPlayThrough
                                        }
                                        onLoadedData={() => {
                                            if (props.onLoadedData) {
                                                props.onLoadedData();
                                            }
                                        }}
                                        onClick={() => {
                                            resetTransform();

                                            if (props.onClickVideo) {
                                                props.onClickVideo();
                                            }
                                        }}
                                    />
                                </VideoWithOverlay>
                            </TransformComponent>
                        )}
                    </TransformWrapper>
                    {!hasHlsSupport && (
                        <h1>
                            HLS not supported. Please contact the service desk
                        </h1>
                    )}
                </>
            );
        },
    ),
);
